import React from 'react';
import { Link } from 'react-router-dom';
import { ReviewType } from 'src/components/BeaconV2Components/ExperimentalLayout/Reviews/ReviewSentiment/Visualizations/ProductReviewGrid';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import { SlButton } from 'src/components/BeaconV2Components/Header/SLDropdownMenu/SlButton';
import ReviewStarsV2 from 'src/components/BeaconV2Components/ProductReviewCard/StarsV2';

import {
  SlDialog,
  SlDialogCloseButton,
  SlDialogContent,
  SlDialogFooter,
  SlDialogHeader,
  SlDialogRoot,
  SlDialogTitle
} from 'src/components/BeaconV2Components/SlDialog';
import { useAppSelector, useQueryParamValue } from 'src/utils/Hooks';
import { addPersistentQueryParams } from 'src/utils/browser';
import { formatTime } from 'src/utils/dateformatting';
import { getProductImageUrl } from 'src/utils/image';

export const ReviewDetailsPopup = ({
  review,
  open,
  onClose
}: {
  review: ReviewType;
  open: boolean;
  onClose: () => void;
}) => {
  const { product, stacklineSku, user, reviewTime, reviewText, reviewTitle } = review;
  const { searchParams, additionalParams } = useAppSelector((state) => state.app.queryParams);
  const tab = useQueryParamValue('tab');
  const subTab = useQueryParamValue('subtab');
  const linkedSubtab = tab !== 'reviews' ? 'reviewMetrics' : subTab;
  const retailer = useAppSelector((state) => state.retailer);
  const mainTimePeriod = useAppSelector((state) => state.mainTimePeriod);
  const app = useAppSelector((state) => state.app);

  const { targetUrl, name } = app;

  const queryParams = `${addPersistentQueryParams(retailer, mainTimePeriod)}&tab=reviews&subtab=${linkedSubtab}`;
  const productUrl = `/product/${stacklineSku}${searchParams}${additionalParams}`;

  // The API omits `product` when the review's SKU is not in the client's Beacon catalog. Such a
  // review cannot be shown as a product card, so skip it entirely.
  if (!product) {
    return null;
  }

  return (
    <SlDialogRoot>
      <SlDialog onClose={onClose} open={open} width={640} height={545}>
        <SlDialogHeader>
          <SlDialogTitle>Customer Review</SlDialogTitle>
          <SlDialogCloseButton onClick={onClose} />
        </SlDialogHeader>

        <SlDialogContent sx={{ overflowX: 'hidden', paddingX: '48px' }}>
          <Flex flexDirection="column" gap={32}>
            <Flex flexDirection="column" gap={16}>
              <Text variant="subtitle1">Review Content</Text>
              <Flex flexDirection="column" gap={8}>
                <Text variant="subtitle2">Title</Text>
                <Text variant="body2" sx={{ lineHeight: 1.43, letterSpacing: 0.08 }}>
                  {reviewTitle}
                </Text>
              </Flex>
              <Flex flexDirection="column" gap={8}>
                <Text variant="subtitle2">Description</Text>
                <Text variant="body2" sx={{ lineHeight: 1.43, letterSpacing: 0.08 }}>
                  {reviewText}
                </Text>
              </Flex>
            </Flex>

            <Flex flexDirection="column" gap={16}>
              <Text variant="subtitle1">Review Details</Text>

              <Flex justifyContent="center" alignItems="flex-start">
                {/* Image */}
                <Link to={productUrl} onClick={(event) => event.stopPropagation()}>
                  <img
                    style={{
                      marginTop: '8px',
                      height: '112px',
                      maxWidth: '112px',
                      objectFit: 'contain'
                    }}
                    src={getProductImageUrl(stacklineSku)}
                    alt={product.title}
                  />
                </Link>

                <Flex flexDirection="column" marginLeft="24px" justifyContent="center">
                  <Flex height="24px" alignItems="center">
                    <Link to={`/brand/${product.brandId}?${queryParams}`}>
                      <Text variant="subtitle1">{product.brandName}</Text>
                    </Link>
                  </Flex>
                  <Flex marginTop="8px" height="40px">
                    <Link to={productUrl}>
                      <Text lineHeight="20px" variant="body2" truncateLines={2}>
                        {product.title}
                      </Text>
                    </Link>
                  </Flex>
                  <Flex marginTop="16px" flexDirection="column">
                    <Flex gap={64}>
                      {/* Ratings */}
                      <Flex width="85px" flexDirection="column">
                        <Flex height="16px">
                          <Text transform="uppercase" color="secondary" variant="subtitle4">
                            RATING
                          </Text>
                        </Flex>
                        <Flex height="19px" marginTop="4px">
                          <ReviewStarsV2 review={review} />
                        </Flex>
                      </Flex>

                      {/* Date */}
                      <Flex width="85px" flexDirection="column">
                        <Flex>
                          <Text color="secondary" transform="uppercase" variant="subtitle4">
                            DATE
                          </Text>
                        </Flex>
                        <Flex marginTop="4px">
                          <Text variant="body2">{formatTime(reviewTime, 'MMM D, YYYY')}</Text>
                        </Flex>
                      </Flex>

                      {/* Customer */}
                      <Flex width="100px" flexDirection="column">
                        <Flex>
                          <Text color="secondary" transform="uppercase" variant="subtitle4">
                            CUSTOMER
                          </Text>
                        </Flex>
                        <Flex marginTop="4px" width="110px">
                          <Text variant="body2" truncateLines={1}>
                            {user.username}
                          </Text>
                        </Flex>
                      </Flex>
                    </Flex>
                  </Flex>
                </Flex>
              </Flex>
            </Flex>
          </Flex>
        </SlDialogContent>

        <SlDialogFooter
          sx={{
            display: 'flex',
            justifyContent: 'space-between'
          }}
        >
          <SlButton onClick={onClose}>Cancel</SlButton>
          <SlButton
            variant="contained"
            target="_blank"
            href={`https://${targetUrl}/api/utility/OpenRetailerProductReviewPage?appName=${name}&retailerId=${retailer.id}&stacklineSku=${review.stacklineSku}&reviewId=${review.reviewId}`}
          >
            View details
          </SlButton>
        </SlDialogFooter>
      </SlDialog>
    </SlDialogRoot>
  );
};
